import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { DataService } from '../../../core/service/base/data.service';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { Observable, catchError, finalize, map, tap } from 'rxjs';
import { BankTransactionImport } from '../../model/bank-transaction-import';
import { LocalDate } from '../../../core/model/local-date';
import { PagedFilter } from '../../../core/model/paged-filter';

export type BankTransactionImportFilter = {
  companyId: string;
  bankAccountNumber: string;
  currencyCode: string;
  debtorName: string;
  transactionText: string;
  importedAtFrom: LocalDate;
  importedAtTo: LocalDate;
  transactionDateFrom: LocalDate;
  transactionDateTo: LocalDate;
  transactionStatus: number | null;
  selectedFields: string[];
  defaultFields: string[];
};

@Injectable()
export class BankTransactionImportService extends DataService<BankTransactionImport> {
  private readonly _defaultFilterFields: string[] = ['bankAccountNumber', 'companyId', 'transactionStatus', 'transactionText'];
  constructor(
    private _client: HttpClient,
    @Inject(APP_CONFIG) private _config: IAppConfig,
    messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(_client, _config, messageService, BankTransactionImport, progressBar, 'bank-transaction-import');
  }

  export(directDebitIds: string[]): Observable<File> {
    return super.exportDataItems(directDebitIds, 'Direct_debit');
  }

  getFilterDefinition(): Observable<PagedFilter<BankTransactionImportFilter>> {
    return this.client
      .get<any>(`${this.endpoint}/filter`)
      .pipe(
        map((res: PagedFilter<BankTransactionImportFilter>) => {
          const result: PagedFilter<BankTransactionImportFilter> = {
            page: res.page,
            pageSize: res.pageSize,
            descending: res.descending,
            filter: {
              bankAccountNumber: res.filter?.bankAccountNumber,
              currencyCode: res.filter?.currencyCode,
              debtorName: res.filter?.debtorName,
              transactionText: res.filter?.transactionText,
              importedAtFrom: res.filter?.importedAtFrom,
              importedAtTo: res.filter?.importedAtFrom,
              transactionDateFrom: res.filter?.transactionDateFrom,
              transactionDateTo: res.filter?.transactionDateTo,
              transactionStatus: Number.isInteger(res.filter?.transactionStatus) ? res.filter?.transactionStatus : null,
              selectedFields: Array.isArray(res.filter?.selectedFields) ? res.filter.selectedFields : this._defaultFilterFields,
              defaultFields: this._defaultFilterFields,
              companyId: res.filter?.companyId
            },
          };
          if (res.orderBy) {
            result.orderBy = res.orderBy;
          }
          return result;
        })
      )
      .pipe(
        catchError(error => {
          console.log(error);
          return [];
        })
      );
  }

  updateBankTransactionStatus(bankTransactionImport: BankTransactionImport, status: any): Observable<any> {
    this.progressBar.start();
    bankTransactionImport.transactionStatus = status;
    return this.client
      .put(`${this.endpoint}`, JSON.stringify(bankTransactionImport), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        tap(() => this.notification.showSuccess('SUCCESS.BANK_IMPORT_TRANSACTIONS_STATUS_UPDATED')),
        catchError(error => {
          this.notification.showError(error);
          throw new Error(`Not possible to update bank transaction status ${status}.`);
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  updateBankTransactionStatusFiltered(
    filter: any,
    includedIds: string[],
    excludedIds: string[],
    affectedItemsCount: number,
    status: any
  ): Observable<any> {
    if (includedIds.length > 0) {
      return this.updateBankTransactionStatusMany(includedIds, status);
    }
    this.progressBar.start();
    return this.client
      .post(
        `${this.endpoint}/update-filtered-status?transactionStatus=${status}`,
        {
          filter: this.prepareFilter(filter),
          includedIds: includedIds,
          excludedIds: excludedIds,
          affectedItemsCount: affectedItemsCount,
        },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        tap(() => this.notification.showSuccess('SUCCESS.BANK_IMPORT_TRANSACTIONS_STATUS_UPDATED')),
        catchError(error => {
          this.notification.showError(error);
          throw new Error(`Not possible to update bank transaction status ${status}.`);
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  updateBankTransactionStatusMany(ids: string[], status: any): Observable<any> {
    this.progressBar.start();
    return this.client
      .put(`${this.endpoint}/update-many-status?transactionStatus=${status}`, JSON.stringify(ids), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        tap(() => this.notification.showSuccess('SUCCESS.BANK_IMPORT_TRANSACTIONS_STATUS_UPDATED')),
        catchError(error => {
          this.notification.showError(error);
          throw new Error(`Not possible to update bank transaction status ${status}.`);
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  bookAsIncorrectPaymentFiltered(
    filter: any,
    includedIds: string[],
    excludedIds: string[],
    affectedItemsCount: number,
    status: any
  ): Observable<any> {
    if (includedIds.length > 0) {
      return this.bookAsIncorrectPaymentMany(includedIds, status);
    }
    this.progressBar.start();
    return this.client
      .post(
        `${this.endpoint}/register-incorrect-payments-filter`,
        {
          filter: this.prepareFilter(filter),
          includedIds: includedIds,
          excludedIds: excludedIds,
          affectedItemsCount: affectedItemsCount,
        },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        tap(() => this.notification.showSuccess('SUCCESS.BANK_IMPORT_TRANSACTIONS_STATUS_UPDATED')),
        catchError(error => {
          this.notification.showError(error);
          throw new Error(`Not possible to update bank transaction status ${status}.`);
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  bookAsIncorrectPaymentMany(ids: string[], status: any): Observable<any> {
    this.progressBar.start();
    return this.client
      .post(`${this.endpoint}/register-incorrect-payments`, JSON.stringify(ids), {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        tap(() => this.notification.showSuccess('SUCCESS.BANK_IMPORT_TRANSACTIONS_STATUS_UPDATED')),
        catchError(error => {
          this.notification.showError(error);
          throw new Error(`Not possible to update bank transaction status ${status}.`);
        }),
        finalize(() => this.progressBar.stop())
      );
  }
}
